// ==================== PERIOD LOCKING SERVICE ====================
// Extracted from database.js - contains all accounting period locking functionality

import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';
// NOTE: Service dependencies commented out to avoid circular imports during refactoring
// import { bankReconciliationService } from './bank-reconciliation';
// import { journalEntriesService } from '../accounting/journal-entries';
// import { invoicesService } from '../accounting/invoices';

export const periodLockingService = {
  async getAccountingPeriods(companyId) {
    const periodsRef = collection(db, 'accountingPeriods');
    const q = query(periodsRef, where('companyId', '==', companyId), orderBy('startDate', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async createAccountingPeriod(periodData) {
    const periodsRef = collection(db, 'accountingPeriods');
    const docRef = await addDoc(periodsRef, {
      ...periodData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'open', // 'open', 'locked', 'closed'
      lockLevel: 'none' // 'none', 'soft', 'hard'
    });
    return docRef.id;
  },

  async updatePeriodStatus(periodId, status, lockLevel = 'none', lockedBy = null) {
    const periodRef = doc(db, 'accountingPeriods', periodId);
    const updateData = {
      status,
      lockLevel,
      updatedAt: serverTimestamp()
    };

    if (status === 'locked' && lockedBy) {
      updateData.lockedBy = lockedBy;
      updateData.lockedAt = serverTimestamp();
    }

    if (status === 'closed' && lockedBy) {
      updateData.closedBy = lockedBy;
      updateData.closedAt = serverTimestamp();
      updateData.lockLevel = 'hard'; // Closed periods are always hard locked
    }

    await updateDoc(periodRef, updateData);
    return true;
  },

  async checkPeriodLock(companyId, transactionDate) {
    const periods = await this.getAccountingPeriods(companyId);
    const txDate = transactionDate?.toDate?.() || new Date(transactionDate);
    
    // Find the period that contains this transaction date
    const period = periods.find(p => {
      const startDate = p.startDate?.toDate?.() || new Date(p.startDate);
      const endDate = p.endDate?.toDate?.() || new Date(p.endDate);
      return txDate >= startDate && txDate <= endDate;
    });

    if (!period) {
      // No period defined for this date - allow transaction but warn
      return {
        isAllowed: true,
        lockLevel: 'none',
        warning: 'No accounting period defined for this date'
      };
    }

    const isAllowed = period.status === 'open' || period.lockLevel === 'none';
    
    return {
      isAllowed,
      lockLevel: period.lockLevel,
      status: period.status,
      period: period,
      message: isAllowed ? null : `Period is ${period.status} (${period.lockLevel} lock)`
    };
  },

  async validateTransactionDate(companyId, transactionDate, userId, userRole = 'user') {
    const lockCheck = await this.checkPeriodLock(companyId, transactionDate);
    
    // Admins can override soft locks
    if (!lockCheck.isAllowed && userRole === 'admin' && lockCheck.lockLevel === 'soft') {
      return {
        isAllowed: true,
        requiresApproval: true,
        message: 'Admin override for soft-locked period'
      };
    }

    // Hard locks and closed periods require special permission
    if (!lockCheck.isAllowed && (lockCheck.lockLevel === 'hard' || lockCheck.status === 'closed')) {
      return {
        isAllowed: false,
        requiresApproval: false,
        message: `Cannot modify transactions in ${lockCheck.status} period`
      };
    }

    return lockCheck;
  },

  async lockPeriod(companyId, startDate, endDate, lockLevel = 'soft', lockedBy) {
    // Check if period already exists
    const periods = await this.getAccountingPeriods(companyId);
    const existingPeriod = periods.find(p => {
      const pStart = p.startDate?.toDate?.() || new Date(p.startDate);
      const pEnd = p.endDate?.toDate?.() || new Date(p.endDate);
      const sDate = startDate?.toDate?.() || new Date(startDate);
      const eDate = endDate?.toDate?.() || new Date(endDate);
      return pStart.getTime() === sDate.getTime() && pEnd.getTime() === eDate.getTime();
    });

    if (existingPeriod) {
      // Update existing period
      return await this.updatePeriodStatus(existingPeriod.id, 'locked', lockLevel, lockedBy);
    } else {
      // Create new period
      const periodData = {
        companyId,
        name: `Period ${startDate?.toISOString?.()?.slice(0, 7) || 'Custom'}`,
        startDate,
        endDate,
        status: 'locked',
        lockLevel,
        lockedBy,
        lockedAt: serverTimestamp()
      };
      return await this.createAccountingPeriod(periodData);
    }
  },

  async closePeriod(companyId, startDate, endDate, closedBy) {
    // Validate that all transactions in period are reconciled
    const validation = await this.validatePeriodClosure(companyId, startDate, endDate);
    
    if (!validation.canClose) {
      throw new Error(`Cannot close period: ${validation.issues.join(', ')}`);
    }

    // Lock the period as closed
    return await this.lockPeriod(companyId, startDate, endDate, 'hard', closedBy);
  },

  async validatePeriodClosure(companyId, startDate, endDate) {
    const issues = [];
    let canClose = true;

    // Check for unreconciled bank transactions
    try {
      // TODO: Reconnect with bankReconciliationService after refactoring
      // const unmatched = await bankReconciliationService.getUnmatchedTransactions(
      //   companyId, null, startDate, endDate
      // );
      // if (unmatched.unmatchedBankTransactions.length > 0 || unmatched.unmatchedJournalEntries.length > 0) {
      //   issues.push(`${unmatched.unmatchedBankTransactions.length + unmatched.unmatchedJournalEntries.length} unreconciled transactions`);
      //   canClose = false;
      // }
    } catch (error) {
      console.warn('Could not validate bank reconciliation:', error);
    }

    // Check for draft journal entries
    // TODO: Reconnect with journalEntriesService after refactoring
    // const journalEntries = await journalEntriesService.getJournalEntries(companyId);
    const journalEntries = []; // Temporary placeholder
    const draftEntries = journalEntries.filter(entry => {
      const entryDate = entry.date?.toDate?.() || new Date(entry.date);
      return entry.status === 'draft' && entryDate >= startDate && entryDate <= endDate;
    });
    
    if (draftEntries.length > 0) {
      issues.push(`${draftEntries.length} unposted journal entries`);
      canClose = false;
    }

    // Check for unpaid invoices older than 90 days
    // TODO: Reconnect with invoicesService after refactoring
    // const invoices = await invoicesService.getInvoices(companyId);
    const invoices = []; // Temporary placeholder
    const oldUnpaidInvoices = invoices.filter(invoice => {
      const dueDate = invoice.dueDate?.toDate?.() || new Date(invoice.dueDate);
      const daysPastDue = (endDate - dueDate) / (1000 * 60 * 60 * 24);
      return (invoice.paymentStatus !== 'paid') && daysPastDue > 90 && dueDate <= endDate;
    });

    if (oldUnpaidInvoices.length > 0) {
      issues.push(`${oldUnpaidInvoices.length} invoices overdue by 90+ days`);
      // This is a warning, not a blocker
    }

    return {
      canClose,
      issues,
      warnings: oldUnpaidInvoices.length > 0 ? ['Old unpaid invoices detected'] : []
    };
  },

  async getPeriodSummary(companyId, startDate, endDate) {
    // Get financial metrics for the period
    // TODO: Reconnect with journalEntriesService after refactoring
    // const journalEntries = await journalEntriesService.getJournalEntries(companyId);
    const journalEntries = []; // Temporary placeholder
    const periodEntries = journalEntries.filter(entry => {
      const entryDate = entry.date?.toDate?.() || new Date(entry.date);
      return entry.status === 'posted' && entryDate >= startDate && entryDate <= endDate;
    });

    let totalRevenue = 0;
    let totalExpenses = 0;
    let totalAssets = 0;
    let totalLiabilities = 0;

    periodEntries.forEach(entry => {
      // Skip entries without lineItems
      if (!entry.lineItems || !Array.isArray(entry.lineItems)) {
        console.warn('⚠️ Journal entry missing lineItems:', entry.id);
        return;
      }
      
      entry.lineItems.forEach(lineItem => {
        const accountCode = lineItem.accountCode;
        const debit = parseFloat(lineItem.debit) || 0;
        const credit = parseFloat(lineItem.credit) || 0;

        if (accountCode.startsWith('4')) { // Revenue
          totalRevenue += credit - debit;
        } else if (accountCode.startsWith('5')) { // Expenses
          totalExpenses += debit - credit;
        } else if (accountCode.startsWith('1')) { // Assets
          totalAssets += debit - credit;
        } else if (accountCode.startsWith('2')) { // Liabilities
          totalLiabilities += credit - debit;
        }
      });
    });

    return {
      period: { startDate, endDate },
      summary: {
        totalRevenue,
        totalExpenses,
        netIncome: totalRevenue - totalExpenses,
        totalAssets,
        totalLiabilities,
        netWorth: totalAssets - totalLiabilities
      },
      entryCount: periodEntries.length,
      transactionCount: periodEntries.reduce((sum, entry) => sum + (entry.lineItems?.length || 0), 0)
    };
  }
}; 